import { zodResolver } from '@hookform/resolvers/zod';
import { ApiClientError } from '@recipes/api-client';
import { resetPasswordRequestSchema, type ResetPasswordRequest } from '@recipes/contracts';
import { Alert, Button } from '@recipes/ui';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import { FormField } from '@/components/forms/form-field';
import { AuthModeLoading, IdentityAccountLink, useAuthMode } from '@/features/auth/identity';
import { PasswordField } from '@/features/profile/password-field';
import { api } from '@/lib/api';
import { describeError } from '@/lib/errors';
import { AuthLayout } from './auth-layout';

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const form = useForm<ResetPasswordRequest>({
    resolver: zodResolver(resetPasswordRequestSchema),
    defaultValues: { token, password: '' },
  });
  const password = form.watch('password');
  const authMode = useAuthMode();

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await api.auth.resetPassword(values);
      setDone(true);
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'TOKEN_INVALID')
        setError(t('auth.errors.tokenInvalid'));
      else setError(describeError(err));
    }
  });

  if (authMode.mode === undefined) return <AuthModeLoading />;
  if (authMode.mode === 'oidc' && authMode.identity) {
    return (
      <AuthLayout
        title={t('auth.resetPassword')}
        description={t('auth.identity.forgotDescription')}
      >
        <IdentityAccountLink
          identity={{ ...authMode.identity, accountUrl: `${authMode.identity.issuer}/forgot` }}
          label={t('auth.resetPassword')}
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('auth.chooseNewPassword')}
      testId="reset-password-page"
      footer={
        <Link to="/login" className="font-medium text-primary underline-offset-4 hover:underline">
          {t('auth.backToLogin')}
        </Link>
      }
    >
      {!token ? (
        <Alert variant="destructive">{t('auth.errors.tokenMissing')}</Alert>
      ) : done ? (
        <div className="space-y-4">
          <Alert variant="success" data-testid="reset-done">
            {t('auth.resetDone')}
          </Alert>
          <Button asChild className="w-full">
            <Link to="/login">{t('auth.login')}</Link>
          </Button>
        </div>
      ) : (
        <form onSubmit={onSubmit} noValidate className="space-y-4">
          {error && <Alert variant="destructive">{error}</Alert>}
          <FormField
            label={t('auth.newPassword')}
            error={form.formState.errors.password && t('auth.errors.passwordWeak')}
            required
          >
            <PasswordField
              autoComplete="new-password"
              value={password}
              showStrength
              data-testid="reset-password"
              {...form.register('password')}
            />
          </FormField>
          <Button
            type="submit"
            className="w-full"
            loading={form.formState.isSubmitting}
            data-testid="reset-submit"
          >
            {t('auth.resetPassword')}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
